/**
 * Service for handling academic plan applications
 */

const ACADEMIC_API_URL =
  process.env.REACT_APP_ACADEMIC_API_URL || 'https://o0vnudbmyc.execute-api.eu-central-1.amazonaws.com';

export interface AcademicApplicationResponse {
  message: string;
  status: 'pending' | 'approved' | 'error';
  error?: string;
  contactSupport?: boolean;
}

export type AcademicStatus = null | 'pending' | 'approved' | 'active';
export type TrialType = 'academic' | 'commercial' | null;
export type UserType = 'academic' | 'commercial' | null;

export interface AcademicStatusResponse {
  status: AcademicStatus;
  groups: string[];
  trialType?: TrialType;
  userType?: UserType;
  activeCampaignLists?: string[];
  activeCampaignTags?: string[];
}

class AcademicApplicationService {
  /**
   * Apply for academic plan (for existing users with expired trial)
   */
  async applyForAcademic(email: string): Promise<AcademicApplicationResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/apply-existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          message: data.error || 'Failed to submit application',
          status: 'error',
          error: data.error,
          contactSupport: data.contactSupport,
        };
      }

      return {
        message: data.message,
        status: data.status || 'pending',
      };
    } catch (error) {
      console.error('Error applying for academic plan:', error);
      return {
        message: 'An error occurred while submitting your application. Please try again.',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get academic status for a user
   * Returns: null (can apply), 'pending' (waiting approval), 'approved' (needs payment), 'active' (is academic)
   */
  async getAcademicStatus(email: string): Promise<AcademicStatusResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/academic-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to get academic status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting academic status:', error);
      return {
        status: null,
        groups: [],
      };
    }
  }

  /**
   * Check if user has academic approval pending payment
   * @deprecated Use getAcademicStatus instead
   */
  hasAcademicApproved(user: any): boolean {
    return user?.['custom:academic_approved'] === 'true';
  }

  /**
   * Check if user has pending academic application
   * @deprecated Use getAcademicStatus instead
   */
  hasAcademicPending(user: any): boolean {
    return user?.['custom:academic_pending'] === 'true';
  }
}

const academicApplicationService = new AcademicApplicationService();
export default academicApplicationService;
